import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { showSuccess } from '../components/GlobalSnackbar';
import { formatFen, effectiveOvertimeWage } from '../utils/money';

const PAGE_SIZE = 50;

export default function WorkLogList() {
  const [workLogs, setWorkLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  // 服务端分页：历史记工随年份线性增长，整表拉取既慢又吃内存
  const [page, setPage] = useState(0); // MUI TablePagination 从 0 计
  const [total, setTotal] = useState(0);
  const [newLog, setNewLog] = useState({
    projectId: '',
    workerId: '',
    date: new Date().toISOString().slice(0, 10),
    dayUnits: '1',
    overtimeHours: '',
    workType: 'big_worker',
    mealAllowance: '',
    workContent: '',
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchOptions = async () => {
    try {
      const [projectsRes, workersRes] = await Promise.all([
        api.get('/api/projects?pageSize=500'),
        api.get('/api/workers'),
      ]);
      setProjects(projectsRes.data.data || projectsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // 服务端分页（后端 /api/work-logs 原生支持 page/pageSize，按日期倒序）
      const logsRes = await api.get(`/api/work-logs?page=${page + 1}&pageSize=${PAGE_SIZE}`);
      setWorkLogs(logsRes.data.data || []);
      setTotal(logsRes.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || error.message || '加载记工记录失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreate = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const worker = workers.find(w => w.id === newLog.workerId);
        const dailyWage = newLog.workType === 'small_worker'
          ? (worker?.smallWorkerDailyWage || 0)
          : (worker?.dailyWage || 0);

        const log = {
          id: Date.now().toString(),
          ...newLog,
          dayUnits: Math.round(parseFloat(newLog.dayUnits || 1) * 100),
          dailyWage,
          mealAllowance: Math.round(parseFloat(newLog.mealAllowance || 0) * 100),
          overtimeHoursTimes100: Math.round(parseFloat(newLog.overtimeHours || 0) * 100),
        };
        await api.post('/api/work-logs', log);
        setOpenDialog(false);
        showSuccess('记工已保存');
        setNewLog({
          projectId: '',
          workerId: '',
          date: new Date().toISOString().slice(0, 10),
          dayUnits: '1',
          overtimeHours: '',
          workType: 'big_worker',
          mealAllowance: '',
          workContent: '',
        });
        fetchData();
      } catch (error) {
        console.error('Error creating work log:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (item) => {
    setEditingItem(item);
    setNewLog({
      projectId: item.projectId || '',
      workerId: item.workerId || '',
      date: item.date || '',
      dayUnits: String((item.dayUnits || 100) / 100),
      overtimeHours: item.overtimeHoursTimes100 ? String(item.overtimeHoursTimes100 / 100) : '',
      workType: item.workType || 'big_worker',
      mealAllowance: item.mealAllowance ? String(item.mealAllowance / 100) : '',
      workContent: item.workContent || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const worker = workers.find(w => w.id === newLog.workerId);
        const dailyWage = newLog.workType === 'small_worker'
          ? (worker?.smallWorkerDailyWage || 0)
          : (worker?.dailyWage || 0);

        const data = {
          ...newLog,
          dayUnits: Math.round(parseFloat(newLog.dayUnits || 1) * 100),
          dailyWage,
          mealAllowance: Math.round(parseFloat(newLog.mealAllowance || 0) * 100),
          overtimeHoursTimes100: Math.round(parseFloat(newLog.overtimeHours || 0) * 100),
        };
        await api.put(`/api/work-logs/${editingItem.id}`, data);
        setOpenDialog(false);
        setEditingItem(null);
        showSuccess('记工已更新');
        setNewLog({
          projectId: '',
          workerId: '',
          date: new Date().toISOString().slice(0, 10),
          dayUnits: '1',
          overtimeHours: '',
          workType: 'big_worker',
          mealAllowance: '',
          workContent: '',
        });
        fetchData();
      } catch (error) {
        console.error('Error updating work log:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/work-logs/${confirmDialog.id}`);
      // 删掉本页最后一条时回退一页，避免停留在空页
      if (workLogs.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        fetchData();
      }
      showSuccess('记工记录已删除');
    } catch (error) {
      console.error('Error deleting work log:', error);
    }
  };


  const getWorkTypeText = (type) => {
    switch (type) {
      case 'big_worker': return '大工';
      case 'small_worker': return '小工';
      case 'helper': return '帮工';
      default: return '其他';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>记工管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchData}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">记工管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingItem(null); setNewLog({ projectId: '', workerId: '', date: new Date().toISOString().slice(0, 10), dayUnits: '1', overtimeHours: '', workType: 'big_worker', mealAllowance: '', workContent: '' }); setOpenDialog(true); }}
        >
          新增记工
        </Button>
      </Box>

      {workLogs.length === 0 && total === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无记工记录
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日期</TableCell>
                <TableCell>项目</TableCell>
                <TableCell>工人</TableCell>
                <TableCell>工种</TableCell>
                <TableCell align="right">出勤</TableCell>
                <TableCell align="right">日工资</TableCell>
                <TableCell align="right">饭补</TableCell>
                <TableCell align="right">合计</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workLogs.map((log) => {
                const workPay = log.dailyWage * log.dayUnits / 100;
                // 加班时薪：记工锁定值优先，回退工人当前值（实现共享于 utils/money）
                const overtimeWage = effectiveOvertimeWage(log);
                const overtimePay = log.overtimeHoursTimes100 ? (log.overtimeHoursTimes100 / 100) * overtimeWage : 0;
                const total = workPay + overtimePay + (log.mealAllowance || 0);
                return (
                  <TableRow key={log.id}>
                    <TableCell>{log.date}</TableCell>
                    <TableCell>{log.projectName || '-'}</TableCell>
                    <TableCell>{log.workerName || '-'}</TableCell>
                    <TableCell>{getWorkTypeText(log.workType)}</TableCell>
                    <TableCell align="right">{(log.dayUnits / 100).toFixed(1)}天</TableCell>
                    <TableCell align="right">{formatFen(log.dailyWage)}</TableCell>
                    <TableCell align="right">{formatFen(log.mealAllowance)}</TableCell>
                    <TableCell align="right">{formatFen(total)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditStart(log)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(log.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelDisplayedRows={({ from, to, count }) => `第 ${from}-${to} 条，共 ${count} 条`}
          />
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '编辑记工' : '新增记工'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="项目"
            fullWidth
            variant="outlined"
            value={newLog.projectId}
            onChange={(e) => setNewLog({ ...newLog, projectId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="工人"
            fullWidth
            variant="outlined"
            value={newLog.workerId}
            onChange={(e) => setNewLog({ ...newLog, workerId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {workers.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="日期"
            fullWidth
            variant="outlined"
            type="date"
            value={newLog.date}
            onChange={(e) => setNewLog({ ...newLog, date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="工种"
            fullWidth
            variant="outlined"
            value={newLog.workType}
            onChange={(e) => setNewLog({ ...newLog, workType: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="big_worker">大工</MenuItem>
            <MenuItem value="small_worker">小工</MenuItem>
            <MenuItem value="helper">帮工</MenuItem>
            <MenuItem value="other">其他</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="出勤（天）"
            fullWidth
            variant="outlined"
            type="number"
            inputProps={{ step: "0.1", min: "0" }}
            value={newLog.dayUnits}
            onChange={(e) => setNewLog({ ...newLog, dayUnits: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="加班（小时）"
            fullWidth
            variant="outlined"
            type="number"
            value={newLog.overtimeHours}
            onChange={(e) => setNewLog({ ...newLog, overtimeHours: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="饭补（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newLog.mealAllowance}
            onChange={(e) => setNewLog({ ...newLog, mealAllowance: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="工作内容"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newLog.workContent}
            onChange={(e) => setNewLog({ ...newLog, workContent: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>取消</Button>
          <Button onClick={editingItem ? handleEditSave : handleCreate} variant="contained" disabled={!newLog.projectId || !newLog.workerId}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="删除记工记录"
        message="确定要删除这条记工记录吗？删除后不可恢复。"
      />
    </Box>
  );
}
